<template>
    <div>
        <div v-if="pending">
            <BaseSpinner />
        </div>
        <template v-else-if="!subsidyCategory" /> <!-- empty case for when there is no entry, nuxt will show an error -->
        <article v-else>
            <HeroBasic
                :title="`${t('subsidies.title_prefix')} ${subsidyCategory?.title.toLowerCase()}`"
                :description="subsidyCategory?.richTextMinimal"
            >
                <template #backlink>
                    <NuxtLink :to="localePath(`/${backlinkUri}`)">
                        <div class="sr-only">{{ t('a11y.return-to') }}</div>
                        <BaseIcon icon="chevron-left" />
                        {{ t('subsidies.backlink') }}
                    </NuxtLink>
                </template>
            </HeroBasic>

            <!-- FILTERS HERE -->
            <BaseSection>
                <BaseGrid class="base-grid--filter">
                    <BaseMultiselect
                        v-model="selectedTargetAudienceCategories"
                        :label="t('subsidies.filters.audience.label')"
                        :inner-label="t('subsidies.filters.audience.placeholder')"
                        :options="targetAudienceCategoryOptions"
                    />

                    <BaseMultiselect
                        v-model="selectedSubsidyPhaseCategories"
                        :label="t('subsidies.filters.phase.label')"
                        :inner-label="t('subsidies.filters.phase.placeholder')"
                        :options="subsidyPhaseCategoryOptions"
                    />
                </BaseGrid>
            </BaseSection>

            <BaseSection
                v-if="!subsidies.length && !pending"
                aria-live="polite"
            >
                <div>{{ t('subsidies.no_results') }}</div>
            </BaseSection>

            <template v-if="subsidies.length > 0 && !pending">
                <template v-for="(group, index) in groupedPhases">
                    <SubsidyGroup
                        v-if="!selectedSubsidyPhaseCategories.length || selectedPhase(group.value)"
                        :key="index"
                        :title="group.label"
                        :subsidies="group?.subsidies"
                    />
                </template>
            </template>
        </article>
    </div>
</template>

<script setup>
import { isEmpty } from 'lodash-es';
import { formatISO } from 'date-fns';

import getSubsidyPages from '~/graphql/queries/getSubsidyPages.graphql';
import getSubsidyCategory from '~/graphql/queries/getSubsidyCategory.graphql';

defineI18nRoute({
    paths: {
        en: '/funding/[category]',
    }
});

const route = useRoute();
const { locale, t } = useI18n();
const localePath = useLocalePath();

const { variables: defaultVariables } = useCraftGraphql();

const currentPage = ref(0);

// subsidyTargetAudience
const { options: targetAudienceCategoryOptions, selected: selectedTargetAudienceCategories } = await useCategory(
    'subsidyTargetAudience',
    'wie',
    currentPage
);

// subsidyPhase
const { options: subsidyPhaseCategoryOptions, selected: selectedSubsidyPhaseCategories } = await useCategory(
    'subsidyPhase',
    'fase',
    currentPage
);

const selectedPhase = (value) => {
    return selectedSubsidyPhaseCategories.value?.includes(value);
};

const categoryVariables = computed(() => {
    return {
        ...defaultVariables,
        uri: undefined, // We don't need the URI for this query
        group: 'subsidyCategory',
        slug: route?.params?.category,
        level: 2
    };
});

const { data: categoryData } = await useAsyncQuery({
    query: getSubsidyCategory,
    variables: categoryVariables
});

const subsidyCategory = computed(() => categoryData.value?.category);

// FILTER FOR GQL
const relatedToCategories = computed(() => {
    const vars = [
        { group: 'subsidyCategory', slug: subsidyCategory.value?.slug, site: defaultVariables.site }
    ];

    if (selectedTargetAudienceCategories.value) {
        for (const item of selectedTargetAudienceCategories.value) {
            vars.push({
                group: 'subsidyTargetAudience',
                slug: item,
                site: defaultVariables.site,
            });
        }
    }

    return vars;
});

const subsidyVariables = computed(() => {
    const now = formatISO(Date.now(), { representation: 'date' });

    const vars = {
        ...defaultVariables,
        uri: undefined, // We don't need the URI for this query
        section: 'subsidies',
        type: 'subsidy',
        now: ['or', `>= ${now}`, null]
    };

    if (!isEmpty(relatedToCategories.value)) {
        vars.relatedToCategories = relatedToCategories.value;
    }

    return vars;
});

const { data: subsidyData, pending } = await useAsyncQuery({
    query: getSubsidyPages,
    variables: subsidyVariables
});

const subsidies = computed(() => subsidyData.value?.entries ?? []);
const backlinkUri = computed(() => locale.value === 'nl' ? 'subsidies' : 'funding');

const groupedPhases = computed(() => {
    const groups = [];

    subsidyPhaseCategoryOptions.value?.forEach((phase) => { // Add an object for each phase in the order from the CMS
        groups.push({
            id: phase.id,
            value: phase.value,
            label: phase.label,
            subsidies: []
        });
    });

    subsidies.value?.forEach((subsidy) => {
        subsidy.subsidyPhaseRelation.forEach((phase) => {
            groups.forEach((group) => {
                if (group.id === phase.id) {
                    group.subsidies.push(subsidy); // Add the subsidy to each group it is part of
                }
            });
        });
    });

    return groups;
});

useHead({
    title:`${t('subsidies.title_head')} | ${t('subsidies.title_prefix')} ${subsidyCategory?.value.title}`
});
</script>
